import { useEffect, useId, useState, type CSSProperties } from "react";
import { DeviceScreen } from "../../shared/device-screen/DeviceScreen.tsx";
import { useMessage } from "../../shared/messages/messages.ts";
import { errorMessage } from "../../shared/api/errors.ts";
import {
  brightnessToPercent,
  deviceLabel,
  deviceName,
  deviceStatusLabel,
  isDeviceOnline,
  percentToBrightness,
  useActiveDevice,
  useDevices,
  useUpdateDisplay,
  useDeviceRuntime,
  type Device,
} from "../../shared/devices/devices.ts";
import { useSession } from "../../shared/session/SessionProvider.tsx";
import { ArtEva } from "../../shared/ui/ArtEva/ArtEva.tsx";
import { Button, ButtonLink } from "../../shared/ui/Button/Button.tsx";
import { WiFiStatusIcon } from "../../shared/ui/icons.tsx";
import { Spinner } from "../../shared/ui/Spinner/Spinner.tsx";
import { DeviceControls, DeviceSelection, useSwipe, useSwitchDevice } from "./DeviceControls.tsx";
import { Owners } from "./Owners.tsx";
import { RenameSheet } from "./RenameSheet.tsx";
import styles from "./EvaPage.module.css";

/** Таб «E-va»: 09.01 — E-va нет, 09.02 — активная E-va, 09.03 — загрузка, 09.04 — E-va offline. */
export function EvaPage() {
  const query = useDevices();
  const { active } = useActiveDevice();
  const { user } = useSession();
  if (query.isError) return <LoadError error={query.error} retry={() => void query.refetch()} />;
  if (!query.data) return <Loading />;
  if (!active) return <NoDevices />;
  const isOwner = active.ownerId === user?.id;
  return (
    <main className={styles.page}>
      <DeviceSelection />
      <ConnectionCard device={active} isOwner={isOwner} />
      {isOwner && <DeviceControls key={`controls:${active.id}`} device={active}><BrightnessCard key={active.id} device={active} /></DeviceControls>}
      {/* key: открытые шторки и ошибки действий не переезжают на другую E-va. */}
      {isOwner && <Owners key={active.id} device={active} />}
      <ButtonLink to="/claim/scan" variant="secondary">Подключить ещё E-va</ButtonLink>
    </main>
  );
}

/** 09.03 / DEVICE / Loading (Figma 1036:4394): Feedback / Loading 48×48 по центру сверху. */
function Loading() {
  return (
    <main className={styles.page}>
      <div className={styles.loading} role="status" aria-label="Загрузка E-va">
        <Spinner size={48} />
      </div>
    </main>
  );
}

function LoadError({ error, retry }: { error: Error; retry: () => void }) {
  return (
    <main className={`${styles.page} ${styles.centered}`}>
      <section className={styles.empty}>
        <p className={styles.emptyDescription} role="alert">
          {errorMessage(error)}
        </p>
        <Button onClick={retry} className={styles.emptyAction}>
          Повторить
        </Button>
      </section>
    </main>
  );
}

/**
 * 09.01 / DEVICE / Empty (Figma 335:1965). «Купить устройство», «Намекнуть близким на подарок» и «Принять инвайт»
 * не выводятся: покупки нет в MVP, а invite принимается по ссылке из приглашения.
 */
function NoDevices() {
  return (
    <main className={`${styles.page} ${styles.centered}`}>
      <section className={styles.empty}>
        <ArtEva width={186} crop="peek" />
        <div className={styles.emptyText}>
          <h1 className={styles.emptyTitle}>Пока здесь тихо...</h1>
          <p className={styles.emptyDescription}>Подключите устройство по QR-коду.</p>
        </div>
        <ButtonLink to="/claim/scan" className={styles.emptyAction}>
          Подключить E-va
        </ButtonLink>
      </section>
    </main>
  );
}

/** Device / Connection Card (Figma 350:5058): название, online-статус и превью экрана; Disconnected — 09.04. */
function ConnectionCard({ device, isOwner }: { device: Device; isOwner: boolean }) {
  const { user } = useSession();
  const titleId = useId();
  const [renaming, setRenaming] = useState(false);
  const online = isDeviceOnline(device);
  const swipe = useSwipe(useSwitchDevice());
  return (
    // key по E-va: при перелистывании карточка въезжает заново.
    <section key={device.id} className={`${styles.card} ${styles.connection}`} aria-labelledby={titleId} {...swipe}>
      <div className={styles.cardText}>
        <div className={styles.headerRow}>
          <h1 id={titleId} className={styles.name}>
            {isOwner ? (
              <button
                type="button"
                className={styles.rename}
                title="Переименовать"
                aria-haspopup="dialog"
                onClick={() => setRenaming(true)}
              >
                {deviceName(device)}
              </button>
            ) : (
              // Чужая E-va подписана владельцем, как в чипе шапки: «Кухня • Мария».
              deviceLabel(device, user?.id)
            )}
          </h1>
          <WiFiStatusIcon className={online ? styles.online : styles.offline} />
        </div>
        <p className={`${styles.status} ${online ? styles.online : styles.offline}`}>{deviceStatusLabel(device)}</p>
      </div>
      {isOwner ? <ReportedScreen device={device} /> : <p className={styles.hint}>Предпросмотр экрана доступен владельцу открытки.</p>}
      {!online && (
        <div className={styles.offlineHint}>
          <p>Открытка не на связи</p>
          {/* В Figma «Режимы и экранные команды попадут в очередь»: режимов в MVP нет, настроек экрана у гостя нет. */}
          <p>{isOwner ? "Сообщения и настройки экрана применятся после подключения" : "Сообщения покажутся после подключения"}</p>
        </div>
      )}
      {isOwner && <RenameSheet device={device} open={renaming} onClose={() => setRenaming(false)} />}
    </section>
  );
}

function ReportedScreen({ device }: { device: Device }) {
  const { data } = useDeviceRuntime(device.id);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);
  const reported = data?.reportedAt ? new Date(data.reportedAt) : null;
  const fresh = isDeviceOnline(device) && data?.onlineStatus === "online" && reported !== null && now - reported.getTime() < 30000;
  const showingMessage = data?.visibleScreen === "message" && !data.mediaLoading;
  const message = useMessage(data?.currentMessageId ?? "", Boolean(showingMessage && data?.currentMessageId));
  const mode = data?.visibleScreen === "clock" ? "clock" : data?.visibleScreen === "pomodoro" ? "pomodoro" : "empty";
  const image = message.data?.screenMediaUrl ?? null;
  return <div className={styles.reportedScreen}>
    <div className={styles.previewLabel}><span>{fresh ? "Сейчас на экране" : "Последнее состояние"}</span><span>{reported ? reported.toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" }) : "Нет данных"}</span></div>
    <div className={styles.preview}>
      {showingMessage && message.data ? <DeviceScreen image={image} imageFit={message.data.imageFit} text={message.data.text} sender={message.data.showSender ? message.data.sender.displayName : null} /> : <DeviceScreen mode={reported ? mode : "empty"} at={fresh ? new Date(now) : reported ?? new Date(now)} timezone={device.timezone} remainingSec={data?.pomodoro.remainingSec} phase={data?.pomodoro.phase === "work" ? "Фокус" : "Перерыв"} placeholder={showingMessage ? "Получаем предпросмотр…" : "Ждём состояние открытки"} />}
    </div>
    {data?.mediaLoading && <p className={styles.loadingNotice}>Фото загружается. Предыдущий экран остаётся видимым.</p>}
    {!fresh && reported && <p className={styles.hint}>Данные могут быть устаревшими. Превью обновится после связи с открыткой.</p>}
  </div>;
}

// Пока ползунок двигают, запрос не отправляется: уходит только положение, на котором палец остановился.
const BRIGHTNESS_DEBOUNCE_MS = 300;

/** Блок «Экран» из Device / Content (Figma 560:3025): яркость в процентах, на устройство уходит 0–255. */
function BrightnessCard({ device }: { device: Device }) {
  const labelId = useId();
  const { mutate, error } = useUpdateDisplay(device.id);
  // Положение ползунка до ответа backend'а; `null` — показываем сохранённую яркость.
  const [draft, setDraft] = useState<number | null>(null);
  const percent = draft ?? brightnessToPercent(device.displayBrightness);

  useEffect(() => {
    if (draft === null) return;
    const timer = setTimeout(() => {
      mutate(
        { displayBrightness: percentToBrightness(draft) },
        {
          onSuccess: () => setDraft((current) => (current === draft ? null : current)),
          onError: () => setDraft(null),
        },
      );
    }, BRIGHTNESS_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, mutate]);

  return (
    <div className={styles.settingsSection}>
      <div className={styles.brightnessHeader}>
        <label id={labelId} className={styles.settingLabel}>Яркость экрана</label>
        <span className={styles.percent}>{percent}%</span>
      </div>
      <input
        type="range"
        className={styles.slider}
        min={0}
        max={100}
        value={percent}
        aria-labelledby={labelId}
        aria-valuetext={`${percent}%`}
        style={{ "--fill": percent / 100 } as CSSProperties}
        onChange={(event) => setDraft(Number(event.target.value))}
      />
      {error && (
        <p className={styles.error} role="alert">
          {errorMessage(error)}
        </p>
      )}
    </div>
  );
}
